import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import ReplayIcon from '@mui/icons-material/Replay';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { ModelSelector } from '../ModelSelector';
import { formatDate, Suggestion, TargetDetail, TargetType } from '../../services/learningApi';
import { RunLink, stepLabel, useApis } from './common';
import { DiffView } from './DiffView';
import { CombineSuggestionsDialog } from './CombineSuggestionsDialog';

interface Props {
  targetType: TargetType;
  targetId: string;
  detail: TargetDetail;
  onChanged: () => void;
}

const KIND_LABELS: Record<string, string> = {
  instructions: 'Instructions',
  parameter: 'Setting',
  model: 'Model',
  node_config: 'Step setting',
  structure: 'Structure',
  knowledge_gap: 'Knowledge gap',
  other: 'Other',
};

const KIND_HELP: Record<string, string> = {
  instructions: 'A rewrite of the function’s instructions (prompt). Applying creates a new function version.',
  parameter: 'A change to a setting such as temperature or max tokens.',
  model: 'Switch to a different AI model.',
  node_config: 'A change to one chain step’s settings.',
  structure: 'A change to how the chain is wired (add, remove, or reorder steps). Done by hand in the chain editor.',
  knowledge_gap: 'Something runs keep needing that no knowledge covers. Add material to a knowledge category, then mark it done.',
  other: 'A general recommendation to handle by hand.',
};

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'default'> = { high: 'error', medium: 'warning', low: 'default' };
const PRIORITY_HELP: Record<string, string> = {
  high: 'Expected to fix a frequent or serious problem. Review these first.',
  medium: 'A worthwhile improvement for a less common problem.',
  low: 'A minor polish or a nice-to-have.',
};
const CONFIDENCE_HELP =
  'How sure the analyzer is that this change will help, based on the runs it reviewed. This is separate from the evidence strength of knowledge entries.';

function show(v: any): string {
  if (v === null || v === undefined || v === '') return '(not set)';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

const NO_IDS: string[] = [];

/** Open suggestions that can go into "Combine & apply". */
function isSelectable(s: Suggestion): boolean {
  return s.status === 'open' && s.auto_applicable && !['blocked', 'changed'].includes(s.freshness?.state ?? '');
}

const SuggestionCard: React.FC<{
  s: Suggestion;
  targetType: TargetType;
  nodeLabel: (id: string | null | undefined) => string | null;
  titleOf: (id: string) => string;
  selected: boolean;
  /** Other open instruction suggestions for the same function. */
  siblings: string[];
  onToggle: (() => void) | null;
  onCombine: (ids: string[]) => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ s, targetType, nodeLabel, titleOf, selected, siblings, onToggle, onCombine, onDone, onError }) => {
  const apis = useApis();
  const change = s.proposed_change || { field: null, current: null, proposed: null };
  const f = s.freshness;
  const isOpen = s.status === 'open';
  const isInstructions = s.kind === 'instructions';
  const isModel = s.kind === 'model';
  const rebased = isInstructions && f?.state === 'rebased';
  const conflict = f?.state === 'conflict';
  const already = f?.state === 'applied';
  const changedSetting = f?.state === 'changed';
  const blocked = f?.state === 'blocked';
  const startValue = rebased ? f?.rebased : change.proposed;
  const [value, setValue] = useState<any>(startValue);
  const [view, setView] = useState<'diff' | 'edit'>('diff');
  const [busy, setBusy] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [confirmSolo, setConfirmSolo] = useState(false);
  const edited = JSON.stringify(value) !== JSON.stringify(startValue);
  const overlapping = (f?.overlaps || []).filter((id) => siblings.includes(id));
  const crowded = isOpen && isInstructions && !already && !conflict && !blocked && siblings.length > 0;
  const where = s.target?.node_id ? nodeLabel(s.target.node_id) : null;
  const originalBefore = show(change.current) === '(not set)' ? '' : String(change.current);
  const liveBefore = rebased ? String(f?.live ?? '') : originalBefore;
  const batched = (s.applied_result?.combined_with?.length ?? 0) > 0;

  const accept = async () => {
    setBusy(true);
    try {
      const res = await apis.learning.acceptSuggestion(
        s.id,
        s.auto_applicable && edited ? value : undefined,
        rebased && edited ? f?.live_function_id : undefined,
      );
      const r = res.result;
      if (r.already_included) onDone(`Already in ${r.version ? `version ${r.version}` : 'the live settings'}, so it was marked as done.`);
      else if (r.manual) onDone('Marked as done.');
      else if (r.new_version) {
        onDone(
          `Applied — created version ${r.new_version}${r.rebased_onto_version ? ', keeping the changes made since this was suggested' : ''}` +
            `${r.nodes_repointed ? ` and updated ${r.nodes_repointed} step(s)` : ''}.`,
        );
      } else onDone('Applied.');
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await apis.learning.dismissSuggestion(s.id);
      onDone('Dismissed. The same suggestion will not be proposed again; you can find it (and reopen it) under Dismissed.');
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await apis.learning.reopenSuggestion(s.id);
      onDone('Reopened — it is back under Open, where you can edit and apply it.');
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setConfirmUndo(false);
    setBusy(true);
    try {
      const { result: r } = await apis.learning.undoSuggestion(s.id);
      const also = r.also_undone ? ` ${r.also_undone} other suggestion${r.also_undone === 1 ? '' : 's'} combined into the same version ${r.also_undone === 1 ? 'was' : 'were'} undone with it.` : '';
      if (r.new_version) {
        onDone(`Undone. Created version ${r.new_version} with the previous ${isInstructions ? 'instructions' : 'value'}${r.nodes_repointed ? ` and updated ${r.nodes_repointed} step(s)` : ''}.${also}`);
      } else {
        onDone(`Undone. The step's ${r.field} is back to ${show(r.value)}. If the chain is open in the editor, reload it before saving there.`);
      }
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const undoable = s.status === 'accepted' && s.auto_applicable && !s.applied_result?.manual;
  const undoInfo = s.applied_result?.undo;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 2, opacity: s.status === 'dismissed' || s.status === 'superseded' ? 0.7 : 1, borderColor: selected ? 'primary.main' : undefined }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        {onToggle && (
          <Tooltip title="Select to apply together with other suggestions">
            <Checkbox size="small" checked={selected} onChange={onToggle} sx={{ p: 0.5, ml: -0.5 }} />
          </Tooltip>
        )}
        <Tooltip title={`${s.priority[0].toUpperCase()}${s.priority.slice(1)} priority: ${PRIORITY_HELP[s.priority] || ''}`}>
          <Chip size="small" label={`${s.priority} priority`} color={PRIORITY_COLORS[s.priority]} />
        </Tooltip>
        <Tooltip title={KIND_HELP[s.kind] || ''}>
          <Chip size="small" label={KIND_LABELS[s.kind] || s.kind} variant="outlined" />
        </Tooltip>
        {where && (
          <Tooltip title="The chain step this change applies to">
            <Chip size="small" label={`Step: ${where}`} variant="outlined" />
          </Tooltip>
        )}
        {s.confidence !== null && (
          <Tooltip title={CONFIDENCE_HELP}>
            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
              {Math.round((s.confidence || 0) * 100)}% confident
            </Typography>
          </Tooltip>
        )}
        {!s.auto_applicable && isOpen && (
          <Tooltip title="This can't be applied automatically. Make the change yourself, then click “Mark as done”.">
            <Chip size="small" label="Manual" variant="outlined" color="info" />
          </Tooltip>
        )}
        {isOpen && rebased && (
          <Tooltip title="Written against an older version. Its edits are in lines nobody has changed since, so they carry over without undoing anything.">
            <Chip size="small" label={`Adapted to v${f?.live_version}`} color="info" variant="outlined" />
          </Tooltip>
        )}
        {isOpen && conflict && <Chip size="small" label={`Conflicts with v${f?.live_version}`} color="warning" />}
        {isOpen && already && <Chip size="small" label="Already applied" color="success" variant="outlined" />}
        {isOpen && changedSetting && <Chip size="small" label="Changed since" color="warning" variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {formatDate(s.created_at)}
        </Typography>
      </Stack>
      <Typography variant="subtitle1" fontWeight={600}>
        {s.title}
      </Typography>
      {s.rationale && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {s.rationale}
        </Typography>
      )}

      {isOpen && blocked && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {f?.message}
        </Alert>
      )}
      {isOpen && rebased && (
        <Alert severity="info" sx={{ mt: 2 }}>
          The instructions changed after this was suggested (now version {f?.live_version}). Its edits are in other lines, so they’re shown here applied to
          the current version — the changes made since are kept.
        </Alert>
      )}
      {isOpen && conflict && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<CallMergeIcon />} onClick={() => onCombine([s.id])}>
              Combine with current
            </Button>
          }
        >
          The instructions changed after this was suggested (now version {f?.live_version}), in the same place this edits
          {f?.conflict_line ? ` (around “${f.conflict_line.slice(0, 80)}”)` : ''}. Applying it as written would undo those changes, so it’s blocked. Combine
          it with the current version instead, or dismiss it.
        </Alert>
      )}
      {isOpen && already && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {isInstructions ? `Version ${f?.live_version} already includes this change.` : `This is already set to ${show(f?.live)}.`} Mark it as done or dismiss it.
        </Alert>
      )}
      {isOpen && changedSetting && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This was changed to {show(f?.live)} after the suggestion was made (it was {show(change.current)}). Enter the value you want below to apply it anyway, or
          dismiss it.
        </Alert>
      )}
      {isOpen && isInstructions && (f?.overlaps?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Edits the same lines as {f!.overlaps!.map((id) => `“${titleOf(id)}”`).join(', ')}. Applying one of them makes the others conflict, so select them
          together and use “Combine &amp; apply”.
        </Alert>
      )}
      {isOpen && isInstructions && !already && (f?.dropped_lines?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This rewrite also deletes {f!.dropped_lines!.length} existing line{f!.dropped_lines!.length === 1 ? '' : 's'} outright. Rewrites often drop rules by
          accident, so check this is intended (use “Edit before applying” to keep them):
          {f!.dropped_lines!.map((l, i) => (
            <Box key={i} component="div" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
              − {l}
            </Box>
          ))}
        </Alert>
      )}

      {s.auto_applicable && !isOpen && (
        <Box sx={{ mt: 2 }}>
          {isInstructions ? (
            <DiffView before={originalBefore} after={String(change.proposed ?? '')} />
          ) : (
            <Typography variant="body2">
              {change.field ? `${change.field}: ` : ''}
              {show(change.current)} → {show(change.proposed)}
            </Typography>
          )}
        </Box>
      )}

      {s.auto_applicable && isOpen && !blocked && (
        <Box sx={{ mt: 2 }}>
          {isInstructions && conflict ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                As suggested, against the older version:
              </Typography>
              <DiffView before={originalBefore} after={String(change.proposed ?? '')} />
            </>
          ) : isInstructions ? (
            <>
              <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} sx={{ mb: 1 }}>
                <ToggleButton value="diff">Changes</ToggleButton>
                <ToggleButton value="edit" disabled={already}>
                  Edit before applying
                </ToggleButton>
              </ToggleButtonGroup>
              {view === 'diff' ? (
                <DiffView before={liveBefore} after={already ? liveBefore : String(value ?? '')} />
              ) : (
                <TextField multiline fullWidth minRows={8} maxRows={24} value={value ?? ''} onChange={(e) => setValue(e.target.value)} InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
              )}
            </>
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <Typography variant="caption" color="text.secondary">
                  Current {change.field ? `(${change.field})` : ''}
                </Typography>
                <Typography variant="body1">{show(changedSetting || already ? f?.live : change.current)}</Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                {isModel ? (
                  <ModelSelector size="small" fullWidth label="Change to" value={String(value ?? '')} onChange={(v) => setValue(v as string)} />
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Change to"
                    value={value ?? ''}
                    type={typeof change.proposed === 'number' ? 'number' : 'text'}
                    onChange={(e) => setValue(typeof change.proposed === 'number' ? Number(e.target.value) : e.target.value)}
                  />
                )}
              </Grid>
            </Grid>
          )}
          {!conflict && !already && (targetType === 'function' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Applying creates a new version of the function; previous versions stay available.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The chain is saved to version history first, so this can be undone. If the chain is open in the editor, reload it before saving there.
            </Typography>
          ))}
        </Box>
      )}

      {!s.auto_applicable && change.proposed && (
        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {show(change.proposed)}
          </Typography>
          {isOpen && (
            <Typography variant="caption" color="text.secondary">
              This needs to be done by hand — mark it done once handled.
            </Typography>
          )}
        </Alert>
      )}

      {s.evidence?.runs && s.evidence.runs.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Based on:
          </Typography>
          {s.evidence.runs.map((r) => (
            <RunLink key={r.id} type={r.type} id={r.id} />
          ))}
        </Stack>
      )}

      {isOpen && crowded && confirmSolo && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Apply only this one?
          </Typography>
          <Typography variant="body2">
            {overlapping.length > 0
              ? `This edits the same lines as ${overlapping.map((id) => `“${titleOf(id)}”`).join(', ')}. Once this is applied, ${overlapping.length === 1 ? 'that one is' : 'those are'} blocked as conflicts and can only be used by combining afterwards.`
              : `${siblings.length} other open suggestion${siblings.length === 1 ? '' : 's'} also change${siblings.length === 1 ? 's' : ''} these instructions. They edit different lines, so they still apply afterwards, but you'd get a separate version for each instead of one to review and test.`}{' '}
            {edited ? 'Your edits are kept only if you apply this one alone.' : ''}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            <Button variant="contained" size="small" startIcon={<CallMergeIcon />} onClick={() => { setConfirmSolo(false); onCombine([s.id, ...siblings]); }}>
              Combine all {siblings.length + 1} instead
            </Button>
            <Button variant="outlined" size="small" color="warning" onClick={() => { setConfirmSolo(false); accept(); }} disabled={busy}>
              Apply only this one
            </Button>
            <Button size="small" onClick={() => setConfirmSolo(false)}>
              Cancel
            </Button>
          </Stack>
        </Alert>
      )}

      {isOpen ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {crowded && (
            <Tooltip title="Review this and the other open suggestions for these instructions as one new version">
              <Button variant="contained" startIcon={<CallMergeIcon />} onClick={() => onCombine([s.id, ...siblings])} disabled={busy}>
                Combine all {siblings.length + 1}
              </Button>
            </Tooltip>
          )}
          <Button
            variant={crowded ? 'outlined' : 'contained'}
            startIcon={busy ? <CircularProgress size={14} /> : <CheckIcon />}
            onClick={crowded ? () => setConfirmSolo(true) : accept}
            disabled={busy || blocked || conflict || (changedSetting && !edited) || confirmSolo}
          >
            {already || !s.auto_applicable
              ? 'Mark as done'
              : crowded
                ? edited ? 'Apply only this (edited)' : 'Apply only this one'
                : edited ? 'Apply edited version' : 'Apply'}
          </Button>
          <Button startIcon={<CloseIcon />} onClick={dismiss} disabled={busy}>
            Dismiss
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            size="small"
            label={
              s.status === 'accepted' && s.applied_result?.already_included
                ? 'already applied'
                : s.status === 'accepted' && s.applied_result?.manual
                  ? 'done'
                  : s.status === 'reverted'
                    ? 'undone'
                    : s.status
            }
            color={s.status === 'accepted' ? 'success' : s.status === 'reverted' ? 'warning' : 'default'}
          />
          {batched && (
            <Tooltip title={s.applied_result!.combined_with.map((id: string) => `“${titleOf(id)}”`).join(', ')}>
              <Chip size="small" variant="outlined" icon={<CallMergeIcon />} label={`Applied with ${s.applied_result!.combined_with.length} other${s.applied_result!.combined_with.length === 1 ? '' : 's'}`} />
            </Tooltip>
          )}
          <Typography variant="caption" color="text.secondary">
            {s.decided_by ? `${s.decided_by} · ` : ''}
            {formatDate(s.decided_at)}
            {s.applied_result?.new_version ? ` · created version ${s.applied_result.new_version}` : ''}
            {undoInfo
              ? ` · undone${undoInfo.by ? ` by ${undoInfo.by}` : ''} ${formatDate(undoInfo.at)}${undoInfo.new_version ? ` (version ${undoInfo.new_version})` : ''}`
              : ''}
          </Typography>
          {undoable && !confirmUndo && (
            <Tooltip title="Put back the value this change replaced. Only that value changes; other edits made since are kept.">
              <span>
                <Button size="small" startIcon={busy ? <CircularProgress size={14} /> : <UndoIcon />} onClick={() => setConfirmUndo(true)} disabled={busy}>
                  Undo
                </Button>
              </span>
            </Tooltip>
          )}
          {confirmUndo && (
            <>
              <Typography variant="caption">
                {targetType === 'function' || isInstructions
                  ? 'This saves a new function version with the previous value.'
                  : 'This sets the step back to its previous value (saved to version history first).'}
                {batched && isInstructions ? ' Other instruction suggestions combined into the same version are undone with it.' : ''}
              </Typography>
              <Button size="small" variant="contained" color="warning" onClick={undo} disabled={busy}>
                Undo change
              </Button>
              <Button size="small" onClick={() => setConfirmUndo(false)} disabled={busy}>
                Cancel
              </Button>
            </>
          )}
          {(s.status === 'dismissed' || s.status === 'superseded' || s.status === 'reverted') && (
            <Button size="small" startIcon={<ReplayIcon />} onClick={reopen} disabled={busy}>
              Reopen
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export const SuggestionsTab: React.FC<Props> = ({ targetType, targetId, detail, onChanged }) => {
  const apis = useApis();
  const [filter, setFilter] = useState('open');
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [combineIds, setCombineIds] = useState<string[] | null>(null);
  const hasConfig = !!detail.config;

  const load = useCallback(async () => {
    if (!hasConfig) return;
    try {
      const res = await apis.learning.suggestions(targetType, targetId, filter === 'accepted' ? 'accepted,reverted' : filter === 'dismissed' ? 'dismissed,superseded' : filter);
      setItems(res.suggestions);
      const ok = new Set(res.suggestions.filter(isSelectable).map((s) => s.id));
      setSelected((prev) => new Set(Array.from(prev).filter((id) => ok.has(id))));
    } catch (e: any) {
      setError(e.message);
    }
  }, [apis, targetType, targetId, filter, hasConfig]);

  useEffect(() => {
    setItems(null);
    load();
  }, [load]);

  const titles = useMemo(() => Object.fromEntries((items || []).map((s) => [s.id, s.title])), [items]);
  const selectable = useMemo(() => (items || []).filter(isSelectable), [items]);
  /** Live function id -> open instruction suggestions that would change it. */
  const sameFunction = useMemo(() => {
    const groups: Record<string, Suggestion[]> = {};
    for (const s of items || []) {
      const fid = s.freshness?.live_function_id;
      if (s.kind === 'instructions' && fid && isSelectable(s) && s.freshness?.state !== 'applied') (groups[fid] = groups[fid] || []).push(s);
    }
    return groups;
  }, [items]);
  const crowdedGroups = Object.values(sameFunction).filter((g) => g.length > 1);
  const siblingsOf = (s: Suggestion) =>
    (sameFunction[s.freshness?.live_function_id ?? ''] || []).filter((x) => x.id !== s.id).map((x) => x.id);

  if (!hasConfig) return <Alert severity="info">Suggestions appear here after the first analysis.</Alert>;

  const nodes = detail.target.nodes || [];
  const nodeLabel = (id: string | null | undefined) => (id ? stepLabel(nodes, id) : null);
  const titleOf = (id: string) => titles[id] || 'another suggestion';
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = selectable.length > 0 && selectable.every((s) => selected.has(s.id));
  const done = (m: string) => {
    setInfo(m);
    setError(null);
    load();
    onChanged();
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="open">Open</ToggleButton>
          <ToggleButton value="accepted">Applied</ToggleButton>
          <ToggleButton value="dismissed">Dismissed</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          Each analysis can propose changes. Nothing changes until you apply one, and applied changes can be undone.
        </Typography>
      </Stack>
      {filter === 'open' &&
        crowdedGroups.map((g) => {
          const overlapping = g.some((s) => (s.freshness?.overlaps?.length ?? 0) > 0 || s.freshness?.state === 'conflict');
          const step = g[0].target?.node_id ? nodeLabel(g[0].target.node_id) : null;
          return (
            <Alert
              key={g[0].freshness!.live_function_id}
              severity="warning"
              variant="filled"
              icon={<CallMergeIcon />}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" variant="outlined" size="small" onClick={() => setCombineIds(g.map((s) => s.id))} sx={{ whiteSpace: 'nowrap' }}>
                  Combine all {g.length}
                </Button>
              }
            >
              <Typography variant="body2" fontWeight={600}>
                {g.length} suggestions change the same instructions{step ? ` (step: ${step})` : ''}. Apply them together, not one by one.
              </Typography>
              <Typography variant="body2">
                {overlapping
                  ? 'Some of them edit the same lines, so applying one blocks the others. '
                  : 'Applied one at a time, each creates its own version. '}
                “Combine all” merges them into one new version you review before saving.
              </Typography>
            </Alert>
          );
        })}
      {filter === 'open' && selectable.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={selected.size > 0 && !allSelected}
            onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((s) => s.id)))}
          />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="body2" fontWeight={500}>
              {selected.size ? `${selected.size} selected` : 'Apply several at once'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selected suggestions become one new version. Edits to different parts of the instructions merge directly; edits to the same lines are combined by
              AI. You review the result before anything is saved.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<CallMergeIcon />} disabled={!selected.size} onClick={() => setCombineIds(Array.from(selected))}>
            Combine &amp; apply
          </Button>
        </Paper>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {!items && <CircularProgress size={24} />}
      {items && items.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {filter === 'open' ? 'No open suggestions. New ones appear after each analysis.' : 'Nothing here.'}
        </Typography>
      )}
      {items?.map((s) => (
        <SuggestionCard
          key={`${s.id}:${s.freshness?.state ?? ''}:${s.freshness?.live_version ?? ''}`}
          s={s}
          targetType={targetType}
          nodeLabel={nodeLabel}
          titleOf={titleOf}
          selected={selected.has(s.id)}
          siblings={filter === 'open' ? siblingsOf(s) : NO_IDS}
          onToggle={filter === 'open' && isSelectable(s) ? () => toggle(s.id) : null}
          onCombine={(ids) => setCombineIds(ids)}
          onError={(m) => setError(m)}
          onDone={done}
        />
      ))}
      <CombineSuggestionsDialog
        open={!!combineIds}
        ids={combineIds ?? NO_IDS}
        titles={titles}
        targetType={targetType}
        targetId={targetId}
        nodeLabel={nodeLabel}
        onClose={() => setCombineIds(null)}
        onApplied={(m) => {
          setCombineIds(null);
          setSelected(new Set());
          done(m);
        }}
      />
    </Box>
  );
};
